import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoTechAudit } from '@/data/sources-seoc';
import type { TechAuditPageRow, TechAuditResult } from '@/types/seoc';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/tech-audit — Technical Site Audit (W3a). Read-only, source-attributed
 * deeper crawl of the project's site, from DataForSEO OnPage.
 *
 * HONESTY + SAFETY INVARIANTS (W3a contract):
 *  - PAID (DataForSEO OnPage): NEVER auto-fires. Project options is a free list
 *    read; the audit runs ONLY on an explicit click. No useQuery is wired to the
 *    paid read, so mounting this page charges nothing.
 *  - Deeper than the existing Site Health page — full crawl with per-page issues.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null metric renders as an em-dash — numbers are NEVER invented.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

const ROW_LIMIT = 100;

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.onpage')) return 'DataForSEO OnPage';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orInt(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}

/** Severity ranking so a page's worst issue can sort/badge sensibly. */
const SEVERITY_RANK: Record<string, number> = { error: 3, warning: 2, notice: 1 };

function worstSeverity(row: TechAuditPageRow): string | null {
  if (row.issues.length === 0) return null;
  let worst: string | null = null;
  let worstRank = 0;
  for (const issue of row.issues) {
    const rank = SEVERITY_RANK[issue.severity.toLowerCase()] ?? 0;
    if (rank > worstRank) {
      worstRank = rank;
      worst = issue.severity;
    }
  }
  return worst ?? row.issues[0].severity;
}

function severityTone(sev: string | null): 'success' | 'danger' | 'warning' | 'neutral' {
  if (sev === null) return 'success';
  const s = sev.toLowerCase();
  if (s === 'error') return 'danger';
  if (s === 'warning') return 'warning';
  return 'neutral';
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'The technical audit isn’t connected yet',
        body: 'A deep site crawl needs the DataForSEO OnPage connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This crawl would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The audit could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The audit could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-subtle bg-canvas px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-1 font-mono text-lg text-text-primary">{value}</div>
    </div>
  );
}

const PAGE_COLUMNS: SortableColumn<TechAuditPageRow>[] = [
  {
    key: 'url',
    header: 'Page',
    sortable: true,
    sortValue: (p) => p.url,
    render: (p) => p.url,
    truncate: true,
    maxWidthClass: 'max-w-[320px]',
    title: (p) => p.url,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'status_code',
    header: 'Status',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (p) => p.status_code,
    render: (p) => orInt(p.status_code),
    cellClassName: 'font-mono text-text-secondary',
  },
  {
    key: 'issues',
    header: 'Issues',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (p) => p.issues.length,
    render: (p) => formatInt(p.issues.length),
    cellClassName: 'font-mono text-text-secondary',
  },
  {
    key: 'severity',
    header: 'Worst',
    sortable: true,
    sortValue: (p) => {
      const w = worstSeverity(p);
      return w === null ? 0 : (SEVERITY_RANK[w.toLowerCase()] ?? 0);
    },
    numeric: true,
    render: (p) => {
      const w = worstSeverity(p);
      return <Badge tone={severityTone(w)}>{w === null ? 'clean' : w}</Badge>;
    },
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'detail',
    header: 'Top issue',
    render: (p) => (p.issues.length === 0 ? '—' : p.issues[0].detail || p.issues[0].type),
    truncate: true,
    maxWidthClass: 'max-w-[320px]',
    title: (p) => (p.issues.length === 0 ? undefined : p.issues[0].detail || p.issues[0].type),
    cellClassName: 'text-text-secondary',
  },
  {
    key: 'load_time_ms',
    header: 'Load (ms)',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (p) => p.load_time_ms,
    render: (p) => orInt(p.load_time_ms),
    cellClassName: 'font-mono text-text-secondary',
  },
];

export function SeoTechAuditPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Free list read — safe on mount. The paid audit is never wired to a query;
  // it runs on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [audit, setAudit] = useState<SectionState<TechAuditResult>>({ status: 'idle' });

  const runAudit = async () => {
    if (projectId === null || audit.status === 'loading') return;
    setAudit({ status: 'loading' });
    try {
      const data = await fetchSeoTechAudit(projectId, ROW_LIMIT);
      setAudit({ status: 'done', data });
    } catch (err) {
      setAudit({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setAudit({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Technical Site Audit" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Technical Site Audit" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Technical Site Audit" />
        <EmptyState
          title="No project to audit yet"
          teach="The technical audit crawls an SEO project's site. Add your first domain on the Projects page, then come back here for a deep on-page crawl."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Technical Site Audit"
        subtitle="A deeper on-page crawl than Site Health — status codes, load times and per-page issues from licensed DataForSEO OnPage data. Loaded on demand, source-attributed, honest about every missing metric."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          The technical audit uses paid provider data, so it’s limited to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO OnPage</Badge>
          <span className="text-sm font-medium text-text-primary">Full-site crawl</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">
              Paid · budget-guarded · 7-day cache · up to {ROW_LIMIT} pages
            </span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Crawl the site and surface each page's status, load time and technical issues. Runs only
          when you press the button — nothing is fetched or charged on load.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={() => void runAudit()}
            disabled={!canManage || audit.status === 'loading'}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {audit.status === 'loading'
              ? 'Crawling…'
              : audit.status === 'done'
                ? 'Re-run audit'
                : 'Run technical audit'}
          </Button>
        </div>

        <div className="mt-4">
          {audit.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — run a technical audit for this project.
            </p>
          )}
          {audit.status === 'loading' && <LoadingState rows={5} />}
          {audit.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{audit.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{audit.panel.body}</p>
            </div>
          )}
          {audit.status === 'done' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 sm:max-w-xl">
                <SummaryTile label="Pages crawled" value={orInt(audit.data.summary.pages_crawled)} />
                <SummaryTile label="Errors" value={orInt(audit.data.summary.errors)} />
                <SummaryTile label="Warnings" value={orInt(audit.data.summary.warnings)} />
              </div>
              {audit.data.pages.length === 0 ? (
                <div className="rounded-md border border-border-subtle bg-canvas p-3">
                  <p className="text-sm text-text-secondary">
                    The crawl returned no pages — an honest empty result, not an error.
                  </p>
                  <div className="mt-2">
                    <SourceFooter
                      source={audit.data.source}
                      fetchedAt={audit.data.fetched_at}
                      cacheHit={audit.data.cache_hit}
                    />
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                  <SortableTable
                    rows={audit.data.pages}
                    columns={PAGE_COLUMNS}
                    getRowKey={(p, i) => `${p.url}-${i}`}
                    minWidthClass="min-w-[900px]"
                    caption="Crawled pages with status, issue count, worst severity and load time"
                  />
                  <SourceFooter
                    source={audit.data.source}
                    fetchedAt={audit.data.fetched_at}
                    cacheHit={audit.data.cache_hit}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
